import {
    createContext,
    FunctionComponent,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router';
import { AuthConfig } from 'softcat:config/auth.ts';
import { SsoConfig } from 'softcat:config/sso.ts';
import { Territory } from 'softcat:config/territory.ts';
import { UserRole } from 'softcat:config/user-role.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { SoftcatApiUtils } from 'softcat:utils/softcat-api.ts';

export type AuthUser = {
    email?: string;
    id: string;
    roles: string[];
    territories: string[];
    username: string;
};

export type ContextDefaults = {
    contextDefaults?: {
        useCognitoLogin?: boolean;
        user?: AuthUser;
    };
};

export type AuthContextType = {
    hasRequiredRole: (requiredRole: UserRole) => boolean;
    isAuthenticated: boolean;
    refreshUser: () => Promise<void>;
    signOut: () => Promise<void>;
    useCognitoLogin?: boolean;
    user: AuthUser | undefined;
};

type IdTokenClaims = {
    'cognito:groups'?: string[];
    'cognito:username'?: string;
    'custom:territories'?: string;
    email?: string;
};

const parseIdToken = (idToken: string): AuthUser | undefined => {
    try {
        const claims = JSON.parse(atob(idToken.split('.')[1])) as IdTokenClaims;
        const username = claims['cognito:username'];

        if (!username) {
            return undefined;
        }

        return {
            email: claims.email,
            id: username,
            roles: claims['cognito:groups'] ?? [],
            territories: claims['custom:territories']?.split(',') ?? [],
            username,
        };
    } catch {
        return undefined;
    }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: FunctionComponent<PropsWithChildren<ContextDefaults>> = ({
    children,
    contextDefaults = {},
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setTerritories } = useTerritory();
    const oidcAuth = useOidcAuth();
    const [user, setUser] = useState<AuthUser | undefined>(contextDefaults.user);

    const oidcAuthReference = useRef(oidcAuth);
    oidcAuthReference.current = oidcAuth;

    const hasRequiredRole = useCallback(
        (requiredRole: UserRole) => {
            const knownRoles = Object.keys(AuthConfig.RoleHierarchy) as UserRole[];
            const userRole = user?.roles.find((r) => {
                return knownRoles.includes(r as UserRole);
            }) as UserRole | undefined;

            if (userRole) {
                return AuthConfig.RoleHierarchy[userRole] >= AuthConfig.RoleHierarchy[requiredRole];
            }

            return false;
        },
        [user?.roles],
    );

    const refreshUser = useCallback(async () => {
        const oidcIdToken = oidcAuthReference.current.user?.id_token;
        const idToken = oidcIdToken;

        const user = idToken ? parseIdToken(idToken) : undefined;

        if (!user) {
            throw new Error('Unable to parse id token');
        }

        const knownRoles = Object.keys(AuthConfig.RoleHierarchy);
        const matchedRoles = user.roles.filter((r) => {
            return knownRoles.includes(r);
        });

        if (matchedRoles.length === 0) {
            throw new Error(AuthConfig.AuthErrors.INSUFFICIENT_PERMISSIONS);
        }

        if (matchedRoles.length === 1 && matchedRoles[0] === UserRole.UPLOADER) {
            throw new Error(AuthConfig.AuthErrors.UPLOADER_ROLE);
        }

        setTerritories(user.territories as Territory[]);

        setUser(user);
    }, [setTerritories]);

    useEffect(() => {
        /* v8 ignore start */
        if (oidcAuth.isAuthenticated && oidcAuth.user?.access_token) {
            SoftcatApiUtils.setTokenProvider(() => {
                return oidcAuth.user?.access_token;
            });

            const isAuthCallback = location.pathname.endsWith('/auth/callback');
            const isLoginPage = location.pathname.endsWith('/login');

            if (!user && !isAuthCallback && !isLoginPage) {
                refreshUser().catch((error: unknown) => {
                    oidcAuth.removeUser();
                    SoftcatApiUtils.resetTokenProvider();

                    const isUploaderRole =
                        error instanceof Error &&
                        error.message === AuthConfig.AuthErrors.UPLOADER_ROLE;
                    const isInsufficientPermissions =
                        error instanceof Error &&
                        error.message === AuthConfig.AuthErrors.INSUFFICIENT_PERMISSIONS;

                    if (isUploaderRole) {
                        navigate('/login', {
                            state: { ssoError: AuthConfig.AuthErrors.UPLOADER_ROLE },
                        });
                    } else if (isInsufficientPermissions) {
                        navigate('/login', {
                            state: { ssoError: AuthConfig.AuthErrors.INSUFFICIENT_PERMISSIONS },
                        });
                    } else {
                        globalThis.location.href = `${SsoConfig.COGNITO_DOMAIN}/logout?client_id=${SsoConfig.COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(SsoConfig.LOGOUT_URI)}`;
                    }
                });
            }
        }
        /* v8 ignore stop */
    }, [
        location.pathname,
        navigate,
        oidcAuth,
        oidcAuth.isAuthenticated,
        oidcAuth.user,
        refreshUser,
        user,
    ]);

    const signOut = useCallback(async () => {
        if (oidcAuth.isAuthenticated) {
            await oidcAuth.removeUser();
            setUser(undefined);
            globalThis.location.href = `${SsoConfig.COGNITO_DOMAIN}/logout?client_id=${SsoConfig.COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(SsoConfig.LOGOUT_URI)}`;
            return;
        }
        setUser(undefined);
        navigate('/login');
    }, [navigate, oidcAuth]);

    const authContextMemo = useMemo<AuthContextType>(() => {
        return {
            hasRequiredRole,
            isAuthenticated: !!user,
            refreshUser,
            signOut,
            useCognitoLogin: contextDefaults.useCognitoLogin || false,
            user,
        };
    }, [hasRequiredRole, refreshUser, signOut, user, contextDefaults.useCognitoLogin]);

    return <AuthContext.Provider value={authContextMemo}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
